import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Settings, Users, CreditCard, QrCode, Car, Database,
  ChevronDown, Home, Menu, X, PanelLeftClose, PanelLeftOpen, BarChart3, LineChart, Activity,
  LogOutIcon,
  Fuel,
  Monitor,
  Landmark,
  Banknote,
  IdCard,
  Network
} from 'lucide-react';
import TetraLogoWhite from '../assets/Logo_Solo_White (2).png';
import { Link } from 'react-router-dom';
import { useWebSocketContext } from '../contexts/WebSocketProvider';
import { logout } from '../contexts/Auth/logout';
import AllUsers from '../pages/AllUsers';
import Settlement from '../pages/Settlement';
const DashboardLayout = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const { isConnected } = useWebSocketContext();
  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const groupId = localStorage.getItem("group_id");
 useEffect(() => {
  setIsMobileMenuOpen(false);

  // Find parent menu that contains the current route
  const parent = menuItems.find(
    item => item.subItems && item.subItems.some(sub => sub.path === location.pathname)
  );

  if (parent) {
    setOpenDropdown(parent.name);  // keep expanded for desktop
  } else {
    setOpenDropdown(null); // collapse only if not in any submenu
  }
}, [location.pathname]);


  const menuItems = [
    {
      name: 'Dashboard',
      icon: <Home className="w-5 h-5" />,
      path: '/'
    },
    {
      name: 'Analytics',
      icon: <LineChart className="w-5 h-5" />,
      path: '/analytics'
    },
    {
      name: 'Live Charging',
      icon: <Activity className="w-5 h-5" />,
      path: '/live-charging'
    },
    {
      name: 'Stations',
      icon: <Fuel className="w-5 h-5" />,
      path: '/charging-stations'
    },
    {
      name: 'Settlement',
      icon: <Banknote className="w-5 h-5" />,
      path: '/settlement'
    },
    {
      name: 'User Management',
      icon: <Users className="w-5 h-5" />,
      subItems: [
        { name: 'Users', path: '/users' },
      ]
    },
    {
      name: 'RFID',
      icon: <IdCard className="w-5 h-5" />,
      path: '/rfid'
    },
    { name: "Logout", action: logout, icon: <LogOutIcon className="w-5 h-5" /> },
  ];

  if (groupId === '1') {
    menuItems.splice(menuItems.length - 1, 0, {
      name: 'Financial',
      icon: <CreditCard className="w-5 h-5" />,
      subItems: [
        { name: 'Charging Sessions', path: '/charging-sessions' },
        { name: 'Wallets', path: '/wallets' },
        { name: 'Payment Transactions', path: '/payment-transactions' },
        { name: 'Wallet Transactions', path: '/wallet-transactions' },
        { name: 'Financial Reconciliation', path: '/financial-reconciliation' }
      ]
    });
    menuItems.splice(menuItems.length - 1, 0, {
      name: 'Station Management',
      icon: <Network className="w-5 h-5" />,
      path: '/station-management'
    });
    menuItems.splice(menuItems.length - 1, 0, {
      name: 'Station-wise Reports',
      subItems: [
        { name: 'Group By stations', path: '/group-by-station' },
        // You can generate these dynamically based on station IDs from your API or localStorage
      ]
    });
    menuItems.splice(menuItems.length - 1, 0, {
      name: 'All Users',
      icon: <Users className="w-5 h-5" />, // use icon, not page component
      path: '/all-users'
    });
    menuItems.splice(menuItems.length - 1, 0,{
      name: 'OTA & Monitoring',
      icon: <Monitor className="w-5 h-5" />,
      path: '/ota-monotoring'
    });
  }

  const getPageTitle = () => {
    const current = menuItems.find(item =>
      item.path === location.pathname ||
      (item.subItems && item.subItems.some(sub => sub.path === location.pathname))
    );

    // pages reached from inside another page rather than the menu
    if (!current && location.pathname === '/HeartbeatDashboard') return 'Heartbeat Monitor';
    if (!current) return 'Dashboard';
    if (current.subItems) {
      const sub = current.subItems.find(sub => sub.path === location.pathname);
      return sub ? sub.name : current.name;
    }
    return current.name;
  };

  const isActive = (path) => location.pathname === path;

  const navItemClass = (active) =>
    `group relative flex items-center w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
      active
        ? "bg-white text-ink font-medium shadow-sm ring-1 ring-neutral-200"
        : "text-neutral-600 hover:bg-neutral-200/70 hover:text-ink"
    } ${isDesktopSidebarOpen ? "" : "md:justify-center"}`;

  const activeBar = (active) =>
    active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r bg-brand-600" />;

  const iconClass = (active) =>
    `flex-shrink-0 [&>svg]:w-5 [&>svg]:h-5 ${active ? "text-brand-600" : "text-neutral-400 group-hover:text-neutral-700"}`;

  const showLabels = isDesktopSidebarOpen || isMobileMenuOpen;

  return (
    <div className="flex flex-col md:flex-row h-screen supports-[height:100dvh]:h-[100dvh] bg-neutral-50 overflow-hidden">
      {/* Mobile Header */}
      <header className="md:hidden flex-shrink-0 bg-white border-b border-neutral-200 px-4 py-3 flex items-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center overflow-hidden">
            <img src={TetraLogoWhite} alt="Tetra Motors Logo" className="w-5 h-5 object-contain" />
          </div>
          <h1 className="text-base font-semibold tracking-tight text-ink">Tetra Motors</h1>
        </div>
        <div className="flex items-center space-x-3 ml-auto">
          <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md text-neutral-600 hover:text-ink hover:bg-neutral-100"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile backdrop */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black/50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`bg-neutral-100 text-ink border-r border-neutral-200 fixed md:relative z-30 h-full flex flex-col transition-all duration-300 ease-in-out ${
        isMobileMenuOpen ? 'w-64 translate-x-0' : '-translate-x-full md:translate-x-0'
      } ${isDesktopSidebarOpen ? 'md:w-64' : 'md:w-[72px]'}`}>
        <div className="h-16 px-4 flex items-center border-b border-neutral-200 relative">
          {/* Close button for mobile */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="md:hidden absolute top-1/2 -translate-y-1/2 right-3 p-2 rounded-md text-neutral-500 hover:text-ink hover:bg-neutral-200"
          >
            <X size={20} />
          </button>

          <div className={`flex items-center w-full ${isDesktopSidebarOpen ? 'justify-between' : 'md:justify-center'}`}>
            <Link to="/" className="flex items-center space-x-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-neutral-900 flex items-center justify-center flex-shrink-0">
                <img src={TetraLogoWhite} alt="Tetra Motors Logo" className="w-5 h-5 object-contain" />
              </div>
              {showLabels && (
                <div className={`min-w-0 ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>
                  <h1 className="text-[15px] font-semibold tracking-tight leading-tight truncate">Tetra Motors</h1>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-brand-600 font-semibold">CMS Dashboard</p>
                </div>
              )}
            </Link>

            {/* Desktop collapse toggle */}
            {isDesktopSidebarOpen && (
              <button
                onClick={() => setIsDesktopSidebarOpen(false)}
                className="hidden md:flex p-1.5 rounded-md text-neutral-400 hover:text-ink hover:bg-neutral-200"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>
        </div>

        {!isDesktopSidebarOpen && (
          <button
            onClick={() => setIsDesktopSidebarOpen(true)}
            className="hidden md:flex mx-auto mt-3 p-2 rounded-md text-neutral-400 hover:text-ink hover:bg-neutral-200"
            aria-label="Expand sidebar"
          >
            <PanelLeftOpen size={18} />
          </button>
        )}

        <nav className="flex-1 overflow-y-auto py-4">
          {showLabels && (
            <p className={`px-6 mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>
              Menu
            </p>
          )}
          <ul className="space-y-0.5 px-3">
            {menuItems.filter((item) => !item.action).map((item) => (
              <li key={item.name}>
                {item.path ? (
                  <Link to={item.path} className={navItemClass(isActive(item.path))} title={item.name}>
                    {activeBar(isActive(item.path))}
                    <span className={iconClass(isActive(item.path))}>{item.icon}</span>
                    {showLabels && <span className={`ml-3 truncate ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>{item.name}</span>}
                  </Link>
                ) : (
                  <div>
                    {(() => {
                      const childActive = item.subItems.some((sub) => isActive(sub.path));
                      return (
                        <button
                          onClick={() => {
                            if (!isDesktopSidebarOpen) setIsDesktopSidebarOpen(true);
                            toggleDropdown(item.name);
                          }}
                          className={`${navItemClass(childActive)} justify-between`}
                          title={item.name}
                        >
                          {activeBar(childActive)}
                          <span className="flex items-center min-w-0">
                            <span className={iconClass(childActive)}>{item.icon || <BarChart3 />}</span>
                            {showLabels && <span className={`ml-3 truncate ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>{item.name}</span>}
                          </span>
                          {showLabels && (
                            <ChevronDown
                              className={`w-4 h-4 flex-shrink-0 text-neutral-400 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''} ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}
                            />
                          )}
                        </button>
                      );
                    })()}
                    {showLabels && openDropdown === item.name && (
                      <ul className={`mt-0.5 mb-1 ml-[22px] pl-3 border-l border-neutral-300 space-y-0.5 ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>
                        {item.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.path}
                              className={`block px-3 py-2 text-[13px] rounded-md transition-colors ${
                                isActive(subItem.path)
                                  ? "text-brand-700 font-medium bg-white shadow-sm ring-1 ring-neutral-200"
                                  : "text-neutral-500 hover:text-ink hover:bg-neutral-200/70"
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer: connection status + logout */}
        <div className="border-t border-neutral-200 p-3 space-y-1">
          {showLabels && (
            <div className={`flex items-center px-3 py-2 text-xs text-neutral-500 ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>
              <span className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
              {isConnected ? 'Live connection' : 'Reconnecting...'}
            </div>
          )}
          {menuItems.filter((item) => item.action).map((item) => (
            <button
              key={item.name}
              onClick={() => {
                if (window.confirm("Are you sure you want to logout?")) {
                  item.action();
                }
              }}
              className={`${navItemClass(false)} hover:!text-red-600 hover:!bg-red-50`}
              title={item.name}
            >
              <span className="flex-shrink-0 [&>svg]:w-5 [&>svg]:h-5">{item.icon}</span>
              {showLabels && <span className={`ml-3 ${isDesktopSidebarOpen ? '' : 'md:hidden'}`}>{item.name}</span>}
            </button>
          ))}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top bar */}
        <header className="hidden md:flex h-16 flex-shrink-0 items-center justify-between px-6 bg-white border-b border-neutral-200">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-neutral-400 font-medium">Tetra Motors CMS</p>
            <h2 className="text-lg font-semibold tracking-tight text-ink leading-tight">{getPageTitle()}</h2>
          </div>
          <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
            isConnected ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
            {isConnected ? 'Connected' : 'Reconnecting'}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

    </div>
  );
};

export default DashboardLayout;